import { useEffect, useState } from "react";
import { Share, X, Zap } from "lucide-react";

const DISMISS_KEY = "sgdfw-install-dismissed";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isInstalled = () =>
  window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInstalled() || localStorage.getItem(DISMISS_KEY)) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(() => e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari never fires beforeinstallprompt - show manual instructions instead
    const iosTimer = isIOS() ? setTimeout(() => setVisible(true), 9000) : null;

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    setDeferred(null);
    setVisible(false);
  };

  return (
    <div
      data-testid="install-banner"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto flex max-w-md items-center gap-3 rounded-xl border border-volt/40 bg-surface/95 p-3 shadow-[0_8px_40px_rgba(0,0,0,0.6)] backdrop-blur-md sm:bottom-5"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <img src="/icons/icon-192.png" alt="" className="h-10 w-10 rounded-lg" />
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm tracking-wide text-white">GET THE SPORTSGEARDFW APP</p>
        {deferred ? (
          <p className="font-score text-[10px] tracking-wider text-slate-400">FREE · LIVE SCORES & ALERTS ON YOUR HOME SCREEN</p>
        ) : (
          <p className="font-score text-[10px] tracking-wider text-slate-400">
            TAP <Share className="inline h-3 w-3 text-volt" /> SHARE, THEN "ADD TO HOME SCREEN"
          </p>
        )}
      </div>
      {deferred && (
        <button
          data-testid="install-btn"
          onClick={install}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-volt px-3 py-2 font-display text-xs tracking-widest text-ink transition-colors hover:bg-white"
        >
          <Zap className="h-3.5 w-3.5" /> INSTALL
        </button>
      )}
      <button data-testid="install-dismiss" onClick={dismiss} aria-label="Dismiss install banner" className="shrink-0 p-1 text-slate-500 transition-colors hover:text-white">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
